import React from 'react';
import { ObjectType } from '@fullstory/proto/fs/services/dx-objects';
import { NamedElement } from '@fullstory/proto/fs/services/search-settings';
import { useObjectViewed } from '@fullstory/library-ui';
import { useNamedElementSignalStudio } from '../../../hooks/useNamedElementSignalStudio';
import { NamedElementDetailsModalProps } from './types';
import { ConfirmSaveModal } from './ConfirmSaveModal';
import { SignalStudioPreviewResolvedEvent } from '@fullstory/signal-studio-context';
import { NamedElementWithPreview } from '../../../types';

type NamedElementModalProps = Omit<
  NamedElementDetailsModalProps,
  | 'details'
  | 'setDetails'
  | 'issues'
  | 'auditHistory'
  | 'activityMonitoring'
  | 'activityMonitoringError'
  | 'namedElementRecord'
  | 'timeRange'
  | 'onTimeRangeChange'
  | 'setSnooze'
  | 'properties'
  | 'propertiesLoading'
  | 'propertiesFailed'
  | 'retryFailedProperties'
  | 'elementPropertyRules'
  | 'objectReferences'
  | 'savedApiName'
  // undefined id for new element flow
> & {
  id?: string;
  duplicate?: boolean;
  initialElement?: Partial<NamedElement>;
  showDataDirect?: boolean;
  isSuggestion?: boolean;
  initialPreview?: SignalStudioPreviewResolvedEvent;
  exploreSuggestedElements?: NamedElementWithPreview[];
  collapsed?: boolean;
};

export const NamedElementModal: React.FC<NamedElementModalProps> = props => {
  useObjectViewed(props.id, ObjectType.ELEMENT, { enabled: !props.isSuggestion });

  return <NamedElementModalSignalStudioImpl data-component="NamedElementModal" {...props} />;
};

const NamedElementModalSignalStudioImpl: React.FC<NamedElementModalProps> = ({
  id,
  duplicate,
  initialElement,
  initialSpecSlugs,
  variant,
  onCloseSignal,
  onSave,
  onArchive,
  onUnarchive,
  onRejectSuggestion,
  disableEdit,
  disableArchive,
  isSuggestion,
  initialPreview,
  exploreSuggestedElements,
  collapsed,
}) => {
  const [showConfirmSaveModal, setShowConfirmSaveModal] = React.useState(false);

  const { SignalStudio, signalStudioProps, saveElement } = useNamedElementSignalStudio({
    id,
    duplicate,
    initialElement,
    initialSpecSlugs,
    variant,
    onCloseSignal,
    onSave,
    onArchive,
    onUnarchive,
    disableEdit,
    disableArchive,
    isSuggestion,
    onRejectSuggestion,
    onConfirm: () => setShowConfirmSaveModal(true),
    initialPreview,
    exploreSuggestedElements,
    collapsed,
  });

  if (!SignalStudio) {
    return null;
  }

  return (
    <>
      <SignalStudio data-component="NamedElementModalSignalStudioImpl" {...signalStudioProps} />
      <ConfirmSaveModal
        open={showConfirmSaveModal}
        onCloseSignal={() => setShowConfirmSaveModal(false)}
        onConfirm={() => {
          setShowConfirmSaveModal(false);
          saveElement();
        }}
      />
    </>
  );
};
